import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, Users, Coins, Film, MessageSquare, Gift, Flag } from 'lucide-react';

const KIND_LABELS = {
  conversation: 'Conversation',
  creation: 'Creation',
  tip: 'Tip',
  cashout: 'Cashout request',
  referral: 'Referral',
};

export default function AdminProfileConnections() {
  const { uid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = async () => {
    try { const r = await api.get(`/admin/users/${uid}/connections`); setData(r.data); }
    catch (e) { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [uid]);

  const del = async (kind, item_id, descriptor) => {
    const kindLabel = KIND_LABELS[kind] || kind;
    if (!window.confirm(`Permanently delete this ${kindLabel}?\n\n${descriptor}\n\nThis cannot be undone.`)) return;
    try {
      await api.post(`/admin/users/${uid}/delete-item`, { kind, item_id });
      toast.success(`${kindLabel} deleted`);
      load();
    } catch { toast.error('Delete failed'); }
  };

  if (!data) return <div className="text-center py-20 text-slate-400">Loading…</div>;
  const u = data.user;
  const c = data.counts;

  return (
    <div className="max-w-5xl mx-auto space-y-5 py-6 px-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav('/admin')} data-testid="admin-conn-back"><ArrowLeft className="w-5 h-5" /></Button>
        <div>
          <h1 className="font-display text-2xl font-bold">@{u.username}</h1>
          <div className="text-xs text-slate-400">{u.display_name} · {u.email || 'no email'} · joined {new Date(u.created_at).toLocaleDateString()}</div>
        </div>
      </div>

      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          ['Chats', c.conversations, MessageSquare],
          ['Messages', c.messages, MessageSquare],
          ['Creations', c.creations, Film],
          ['Followers', c.followers, Users],
          ['Following', c.following, Users],
          ['Cashouts', c.cashouts, Coins],
          ['Tips sent', c.tips_sent, Gift],
          ['Tips received', c.tips_received, Gift],
          ['Orders bought', c.orders_bought, Flag],
          ['Orders sold', c.orders_sold, Flag],
          ['Referrals', c.referrals_made, Users],
          ['Tx log', c.jxp_transactions, Coins],
        ].map(([l, v, I]) => (
          <Card key={l} className="glass p-3">
            <I className="w-3.5 h-3.5 text-violet-400 mb-1" />
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{l}</div>
            <div className="font-display text-xl font-black">{v || 0}</div>
          </Card>
        ))}
      </section>

      {['conversations', 'creations', 'cashouts', 'tips_sent', 'tips_received'].map(section => {
        const items = data.recent[section] || [];
        if (items.length === 0) return null;
        const kindMap = { conversations: 'conversation', creations: 'creation', cashouts: 'cashout', tips_sent: 'tip', tips_received: 'tip' };
        return (
          <section key={section}>
            <h2 className="font-display font-bold text-lg capitalize mb-2">{section.replace('_',' ')}</h2>
            <Card className="glass p-2">
              {items.map(it => {
                const title = it.title || it.prompt || it.method || it.message || it.to_username || it.from_username || it.id;
                const sub = it.created_at ? new Date(it.created_at).toLocaleString() : '';
                return (
                  <div key={it.id} data-testid={`admin-item-${it.id}`} className="flex items-center gap-3 p-3 border-b border-white/5 last:border-0">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{String(title).slice(0, 80)}</div>
                      <div className="text-xs text-slate-400">{sub} · {it.id.slice(0, 8)}</div>
                    </div>
                    {it.url && <img src={it.url} alt="" className="w-10 h-10 object-cover rounded" />}
                    <Button data-testid={`admin-delete-${it.id}`} size="sm" variant="outline" onClick={() => del(kindMap[section], it.id, String(title).slice(0, 80))} className="text-red-400 border-red-500/30 hover:bg-red-500/10">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                );
              })}
            </Card>
          </section>
        );
      })}
    </div>
  );
}
